import type { NegotiationVariableContext } from './core/context'
import type { NegotiationVariable } from './core/define'
import { applyMask } from './core/masks'
import { assertRegistryIntegrity } from './core/security'
import type { MaskKey, VariableCategory } from './core/types'
import { CURRENCY_VARIABLES } from './domains/currency.variables'
import { LUGAR_VARIABLES } from './domains/lugar.variables'
import {
  ADVISOR_VARIABLES,
  DOCUMENT_VARIABLES,
  OTHER_VARIABLES,
  OWNER_VARIABLES,
  PROJECT_VARIABLES,
  SOCIETY_VARIABLES
} from './domains/misc.variables'
import { NEGOTIATION_VARIABLES } from './domains/negotiation.variables'
import { PROPERTY_VARIABLES } from './domains/property.variables'
import { CIERRE_VARIABLES, REPRESENTANTE_VARIABLES } from './domains/representante.variables'
import { TITULAR_VARIABLES } from './domains/titular.variables'

/**
 * Registry central de variables. Compone los dominios. Para exponer un dato
 * nuevo, agregá un defineVariable en el dominio que corresponda — acá no se
 * toca nada salvo sumar un dominio entero.
 */
export const ALL_VARIABLES: NegotiationVariable[] = [
  ...SOCIETY_VARIABLES,
  ...REPRESENTANTE_VARIABLES,
  ...PROJECT_VARIABLES,
  ...PROPERTY_VARIABLES,
  ...NEGOTIATION_VARIABLES,
  ...LUGAR_VARIABLES,
  ...CURRENCY_VARIABLES,
  ...OWNER_VARIABLES,
  ...TITULAR_VARIABLES,
  ...ADVISOR_VARIABLES,
  ...DOCUMENT_VARIABLES,
  ...CIERRE_VARIABLES,
  ...OTHER_VARIABLES
]

// GUARDRAIL (solo DEV): valida integridad + frontera de seguridad al cargar. Si un dev agrega una
// variable sensible/malformada, el editor falla al instante con un mensaje claro — no en producción
// ni en un PDF roto. En build se elimina (tree-shake).
//
// ⚠️ Barre LOS DOS catálogos, no sólo éste: `assertRegistryIntegrity` delega la mitad del metadato
// en `@idc-forms/schemas/variables/catalog-integrity`, que recorre `ALL_DECLARED_CATALOGS` — el de
// documentos y el del binding de Créditos. Hasta el ADR 0036 el de Créditos no lo barría nadie.
if (import.meta.env?.DEV) assertRegistryIntegrity(ALL_VARIABLES)

// Índice por key para lookups O(1).
const BY_KEY = new Map<string, NegotiationVariable>(ALL_VARIABLES.map((v) => [v.key, v]))

export const findVariable = (key: string): NegotiationVariable | undefined => BY_KEY.get(key)

export const variablesByCategory = (): Record<VariableCategory, NegotiationVariable[]> => {
  const out = {} as Record<VariableCategory, NegotiationVariable[]>
  for (const v of ALL_VARIABLES) {
    let bucket = out[v.category]
    if (!bucket) {
      bucket = []
      out[v.category] = bucket
    }
    bucket.push(v)
  }
  return out
}

/**
 * Resuelve TODAS las variables contra un contexto → mapa plano key→valor crudo.
 * Esto reemplaza a generateContentVariablesNegotiation: el orquestador arma el
 * ctx (carga async) una vez y llama acá.
 */
export const resolveAllVariables = (ctx: NegotiationVariableContext): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const v of ALL_VARIABLES) {
    try {
      out[v.key] = v.resolve(ctx) ?? ''
    } catch {
      out[v.key] = ''
    }
  }
  return out
}

/** Resuelve una sola variable y aplica una máscara opcional (para el render). */
export const resolveVariableWithMask = (
  key: string,
  ctx: NegotiationVariableContext,
  mask?: MaskKey
): string => {
  const def = BY_KEY.get(key)
  if (!def) return ''
  return applyMask(def.resolve(ctx), mask)
}
